import type { CallLog } from "@models/call";
import { ConversationTranscriptItemType } from "../../angular-contracts";

/** GCS widget-chat URL parsed for transcript bubbles (same rules as widget-host). */
export type TranscriptAttachmentPreview = {
  url: string;
  fileName: string;
  isImage: boolean;
  isAudio: boolean;
  isVideo: boolean;
};

/** Extensions browsers commonly play inline via `<video>`. */
const BROWSER_VIDEO_PREVIEW_RE = /\.(mp4|m4v|mov|webm|ogv|3gp)(\?|$)/i;

/** Audio-only extensions (`mp4` is video — not listed here). */
const BROWSER_AUDIO_PREVIEW_RE = /\.(mp3|m4a|wav|flac|opus|ogg)(\?|$)/i;

const IMAGE_PREVIEW_RE = /\.(png|jpe?g|gif|webp|svg|bmp|ico)(\?|$)/i;

function attachmentExtMatches(path: string, fileName: string, re: RegExp): boolean {
  return re.test(path) || re.test(fileName);
}

function parseGcsAttachmentPathParts(pathOrUrl: string): { path: string; fileName: string } | undefined {
  try {
    const u = new URL(pathOrUrl);
    const path = u.pathname;
    const seg = path.split("/").pop() || "file";
    const fileName = decodeURIComponent(seg.replace(/\+/g, " "));
    return { path, fileName };
  } catch {
    const path = pathOrUrl;
    const seg = path.split("/").pop() || "file";
    return { path, fileName: seg };
  }
}

/** True when the URL/path looks like a video the browser can preview. */
export function isBrowserPreviewableVideoAttachment(pathOrUrl: string): boolean {
  const parts = parseGcsAttachmentPathParts(pathOrUrl);
  if (!parts) return false;
  return attachmentExtMatches(parts.path, parts.fileName, BROWSER_VIDEO_PREVIEW_RE);
}

/** True when the URL/path looks like audio (not video). */
export function isBrowserPreviewableAudioAttachment(pathOrUrl: string): boolean {
  if (isBrowserPreviewableVideoAttachment(pathOrUrl)) return false;
  const parts = parseGcsAttachmentPathParts(pathOrUrl);
  if (!parts) return false;
  return attachmentExtMatches(parts.path, parts.fileName, BROWSER_AUDIO_PREVIEW_RE);
}

/** Roles a transcript row can have. Values match the API/worker contract strings. */
export enum ConversationTranscriptRole {
  User = "user",
  Assistant = "assistant",
  Human = "human",
  /** Tool/system row recorded in the transcript only (tool name + params); not a real message. */
  Tool = "tool",
}

/** Live typing row rendered at the bottom of the transcript (from {@link LivechatMultiRoomService.onTyping}). */
export type TranscriptTypingIndicator = {
  key: string;
  role:
    | ConversationTranscriptRole.User
    | ConversationTranscriptRole.Human
    | ConversationTranscriptRole.Assistant;
  label: string;
  phase?: "generating" | "tools";
  hint?: string | null;
};

export type TranscriptMessageLine = {
  role: string;
  message: string;
  senderName?: string;
  /** UTC ISO-8601 from API, e.g. 2026-05-25T10:46:52.135Z */
  timestamp?: string;
  /** Structured detail for tool/system rows (e.g. { type, tool, params }). */
  metadata?: Record<string, unknown>;
};

export type ConversationDisplayItem =
  | {
      type: ConversationTranscriptItemType.SessionDivider;
      sessionId: string;
      label: string;
      startTime: Date;
      key: string;
    }
  | {
      type: ConversationTranscriptItemType.DayDivider;
      sessionId: string;
      label: string;
      key: string;
    }
  | {
      type: ConversationTranscriptItemType.Message;
      role: string;
      message: string;
      sessionId: string;
      key: string;
      senderName?: string;
      timestamp?: string;
      /** Present on tool/system rows (e.g. { type, tool, params }). */
      metadata?: Record<string, unknown>;
    }
  | {
      type: ConversationTranscriptItemType.CallEndedDivider;
      sessionId: string;
      key: string;
      conversationEndedBy?: string;
      startTime?: Date;
      endTime?: Date;
      duration?: number;
    };

/** Calendar day in the visitor's browser timezone (for grouping). */
export function transcriptLocalDayKey(isoUtc: string): string {
  const d = new Date(isoUtc.trim());
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Center pill between messages when the local calendar day changes (WhatsApp-style). */
export function formatTranscriptDateDividerLabel(isoUtc: string): string {
  const d = new Date(isoUtc.trim());
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 6);
  if (d >= weekAgo) {
    return new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(d);
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(d);
}

/** Per-message time on the bubble, in the browser's local timezone. */
export function formatTranscriptMessageTime(isoUtc: string | undefined | null): string | null {
  const raw = typeof isoUtc === "string" ? isoUtc.trim() : "";
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/** Append transcript lines with optional day dividers and timestamps on message items. */
export function appendTranscriptMessagesToDisplayItems(
  items: ConversationDisplayItem[],
  sessionId: string,
  messages: TranscriptMessageLine[],
  keyPrefix: string,
): void {
  let lastDay: string | null = null;
  messages.forEach((m, mi) => {
    const ts = m.timestamp?.trim();
    if (ts) {
      const day = transcriptLocalDayKey(ts);
      if (day && day !== lastDay) {
        lastDay = day;
        const label = formatTranscriptDateDividerLabel(ts);
        if (label) {
          items.push({
            type: ConversationTranscriptItemType.DayDivider,
            sessionId,
            label,
            key: `${keyPrefix}-day-${day}-${mi}`,
          });
        }
      }
    }
    items.push({
      type: ConversationTranscriptItemType.Message,
      role: m.role,
      message: m.message,
      sessionId,
      key: `${keyPrefix}-msg-${mi}`,
      ...(m.senderName ? { senderName: m.senderName } : {}),
      ...(ts ? { timestamp: ts } : {}),
      ...(m.metadata ? { metadata: m.metadata } : {}),
    });
  });
}

/**
 * Parse transcript history from a call log (for display). Same rules as contact profile.
 */
export function parseTranscriptFromCallLog(
  callLog: CallLog,
): TranscriptMessageLine[] {
  const raw = callLog?.transcription?.json;
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const history = parsed?.history ?? [];
    return history
      .filter((e: { message?: unknown; content?: unknown }) => {
        const msg = e?.message ?? e?.content;
        return msg != null && String(msg).trim() !== "";
      })
      .map((e: { role?: unknown; message?: unknown; content?: unknown; senderName?: unknown; sender_name?: unknown; timestamp?: unknown; sentAt?: unknown; metadata?: unknown }) => {
        const role = normalizeContactTranscriptRole(e.role);
        const message = String(e.message ?? e.content ?? "").trim();
        const rawTs = e.timestamp ?? e.sentAt;
        const timestamp =
          rawTs != null && String(rawTs).trim() !== "" ? String(rawTs).trim() : undefined;
        const metadata =
          e.metadata != null && typeof e.metadata === "object"
            ? (e.metadata as Record<string, unknown>)
            : undefined;

        // Tool/system rows: keep the role + metadata, no sender-name enrichment.
        if (role === ConversationTranscriptRole.Tool) {
          const toolRow: TranscriptMessageLine = { role, message };
          if (timestamp) toolRow.timestamp = timestamp;
          if (metadata) toolRow.metadata = metadata;
          return toolRow;
        }

        const rawSn = e.senderName ?? e.sender_name;
        let senderName =
          rawSn != null && String(rawSn).trim() !== "" ? String(rawSn).trim() : undefined;
        if (senderName && isPlaceholderSenderName(senderName)) {
          senderName = undefined;
        }
        const enriched = enrichContactTranscriptLine(role, message, senderName);
        const row: { role: string; message: string; senderName?: string; timestamp?: string } = {
          role,
          message: enriched.message,
        };
        if (enriched.senderName) {
          row.senderName = enriched.senderName;
        }
        if (timestamp) {
          row.timestamp = timestamp;
        }
        return row;
      });
  } catch {
    return [];
  }
}

/**
 * First GCS public object URL in the message (path-style or virtual-hosted).
 */
function extractFirstGcsPublicUrl(raw: string | undefined | null): string | undefined {
  const s = typeof raw === "string" ? raw : String(raw ?? "");
  if (!s.trim()) return undefined;
  const patterns = [
    /https:\/\/storage\.googleapis\.com\/[^\s<>'"]+/gi,
    /https:\/\/[a-z0-9][a-z0-9_.-]*\.storage\.googleapis\.com\/[^\s<>'"]+/gi,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (m?.[0]) {
      return m[0].replace(/[),.;}\]]+$/u, "");
    }
  }
  return undefined;
}

/**
 * Transcript bubble body: full text, or caption with the first GCS URL removed
 * when {@link buildTranscriptAttachmentPreview} renders a preview.
 */
export function buildTranscriptDisplayBody(messageText: string | undefined | null): string {
  const raw = typeof messageText === "string" ? messageText : String(messageText ?? "");
  const url = extractFirstGcsPublicUrl(raw);
  if (!url) return raw.trim();
  return raw.split(url).join("").trim().replace(/\n{3,}/g, "\n\n");
}

/** GCS public URL in message → image, audio, or file preview. */
export function buildTranscriptAttachmentPreview(
  messageText: string | undefined | null,
): TranscriptAttachmentPreview | undefined {
  const t = extractFirstGcsPublicUrl(messageText);
  if (!t) return undefined;
  try {
    const u = new URL(t);
    const path = u.pathname;
    const seg = path.split("/").pop() || "file";
    const fileName = decodeURIComponent(seg.replace(/\+/g, " "));
    const isVideo = attachmentExtMatches(path, fileName, BROWSER_VIDEO_PREVIEW_RE);
    const isAudio =
      !isVideo && attachmentExtMatches(path, fileName, BROWSER_AUDIO_PREVIEW_RE);
    const isImage =
      !isVideo &&
      !isAudio &&
      attachmentExtMatches(path, fileName, IMAGE_PREVIEW_RE);
    return { url: t, fileName, isImage, isAudio, isVideo };
  } catch {
    return undefined;
  }
}

/**
 * Some API rows omit `senderName` but embed the same "Human — Name" / "Visitor — Name" line in `message`.
 */
function enrichContactTranscriptLine(
  role:
    | ConversationTranscriptRole.User
    | ConversationTranscriptRole.Human
    | ConversationTranscriptRole.Assistant,
  message: string,
  senderName: string | undefined,
): { message: string; senderName?: string } {
  let msg = message;
  let sn = senderName;
  if (sn && isPlaceholderSenderName(sn)) {
    sn = undefined;
  }

  if (role === ConversationTranscriptRole.Human) {
    const re = /^\s*Human\s*[—\-:–]\s*([^\r\n]+?)\s*(?:\r\n|\n|\r|$)/iu;
    const match = msg.match(re);
    if (match) {
      const nameInLine = match[1]?.trim();
      if (nameInLine && !isPlaceholderSenderName(nameInLine) && !sn) {
        sn = nameInLine;
      }
      msg = msg.slice(match[0].length).trimStart();
    }
  }

  if (role === ConversationTranscriptRole.User) {
    const re = /^\s*Visitor\s*[—\-:–]\s*([^\r\n]+?)\s*(?:\r\n|\n|\r|$)/iu;
    const match = msg.match(re);
    if (match) {
      const nameInLine = match[1]?.trim();
      if (
        nameInLine &&
        nameInLine.toLowerCase() !== "visitor" &&
        (!sn || sn === "Visitor")
      ) {
        sn = nameInLine;
      }
      msg = msg.slice(match[0].length).trimStart();
    }
  }

  const out: { message: string; senderName?: string } = { message: msg.trim() };
  if (sn && !(role === ConversationTranscriptRole.User && sn === "Visitor")) {
    out.senderName = sn;
  }
  return out;
}

function normalizeContactTranscriptRole(raw: unknown): ConversationTranscriptRole {
  const x = String(raw ?? "user")
    .trim()
    .toLowerCase();
  // Tool/system rows recorded in the transcript only (tool name + params).
  if (x === ConversationTranscriptRole.Tool) {
    return ConversationTranscriptRole.Tool;
  }
  if (["assistant", "agent", "ai", "bot", "system"].includes(x)) {
    return ConversationTranscriptRole.Assistant;
  }
  if (["human", "human_agent", "staff", "operator", "team"].includes(x)) {
    return ConversationTranscriptRole.Human;
  }
  if (["user", "customer", "visitor", "enduser", "client"].includes(x)) {
    return ConversationTranscriptRole.User;
  }
  return ConversationTranscriptRole.User;
}

function isPlaceholderSenderName(s: string): boolean {
  const x = s.trim().toLowerCase();
  return x === "" || x === "human" || x === "user" || x === "assistant";
}

export function parseMarkdownAndImagesToHtml(text: string | undefined | null): string {
  const raw = text ?? '';
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  
  let withBold = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  const isImageUrl = (url: string): boolean => {
    const cleanUrl = url.split(/[?#]/)[0] || '';
    return /\.(?:webp|png|jpg|jpeg|gif|svg)$/i.test(cleanUrl);
  };

  // 1. Parse markdown images: ![alt](url)
  withBold = withBold.replace(/!\[([^\]\n]*)\]\((https?:\/\/[^\s)]+)\)/gi, (match, alt, url) => {
    if (isImageUrl(url)) {
      return `<div class="tg-chat-image-container" style="position: relative; display: block; width: fit-content; max-width: 100%; margin: 6px 0;"><img src="${url}" class="tg-chat-inline-img cursor-zoom-in" alt="${alt || 'Image'}" style="max-height: 200px; max-width: 100%; object-fit: contain; display: block; border-radius: 8px; border: 1px solid rgba(0,0,0,0.1);" /><a href="${url}" target="_blank" download class="tg-chat-img-download-btn" style="display: inline-flex; align-items: center; gap: 4px; margin-top: 4px; font-size: 11px; color: #6366f1; text-decoration: none; font-weight: 500; transition: opacity 0.2s;" onmouseover="this.style.opacity=0.8" onmouseout="this.style.opacity=1"><i class="pi pi-download" style="font-size: 10px;"></i> Download</a></div>`;
    }
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline text-indigo-500 hover:text-indigo-600">${alt || url}</a>`;
  });

  // 2. Parse markdown links: [label](url)
  withBold = withBold.replace(/\[([^\]\n]*)\]\((https?:\/\/[^\s)]+)\)/gi, (match, label, url) => {
    if (isImageUrl(url)) {
      const alt = (label && !label.startsWith('http')) ? label : 'Image';
      return `<div class="tg-chat-image-container" style="position: relative; display: block; width: fit-content; max-width: 100%; margin: 6px 0;"><img src="${url}" class="tg-chat-inline-img cursor-zoom-in" alt="${alt}" style="max-height: 200px; max-width: 100%; object-fit: contain; display: block; border-radius: 8px; border: 1px solid rgba(0,0,0,0.1);" /><a href="${url}" target="_blank" download class="tg-chat-img-download-btn" style="display: inline-flex; align-items: center; gap: 4px; margin-top: 4px; font-size: 11px; color: #6366f1; text-decoration: none; font-weight: 500; transition: opacity 0.2s;" onmouseover="this.style.opacity=0.8" onmouseout="this.style.opacity=1"><i class="pi pi-download" style="font-size: 10px;"></i> Download</a></div>`;
    }
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline text-indigo-500 hover:text-indigo-600">${label || url}</a>`;
  });

  // 3. Parse raw URLs (skipping already parsed HTML tags)
  const tagOrUrlRegex = /(<[^>]+>)|(?:([\`\'\"]?)(https?:\/\/[^\s()<>\'\"\`]+)\2)/gi;
  withBold = withBold.replace(tagOrUrlRegex, (match, tag, quote, url) => {
    if (tag) {
      return tag; // Return HTML tag untouched
    }
    if (url) {
      // Strip trailing punctuation like .,;:!? from the URL for rendering, but keep it in the text
      let cleanUrl = url;
      let trailingPunctuation = '';
      const puncMatch = url.match(/([.,;:!?]+)$/);
      if (puncMatch) {
        trailingPunctuation = puncMatch[1];
        cleanUrl = url.substring(0, url.length - trailingPunctuation.length);
      }

      if (isImageUrl(cleanUrl)) {
        return `<div class="tg-chat-image-container" style="position: relative; display: block; width: fit-content; max-width: 100%; margin: 6px 0;"><img src="${cleanUrl}" class="tg-chat-inline-img cursor-zoom-in" alt="Image" style="max-height: 200px; max-width: 100%; object-fit: contain; display: block; border-radius: 8px; border: 1px solid rgba(0,0,0,0.1);" /><a href="${cleanUrl}" target="_blank" download class="tg-chat-img-download-btn" style="display: inline-flex; align-items: center; gap: 4px; margin-top: 4px; font-size: 11px; color: #6366f1; text-decoration: none; font-weight: 500; transition: opacity 0.2s;" onmouseover="this.style.opacity=0.8" onmouseout="this.style.opacity=1"><i class="pi pi-download" style="font-size: 10px;"></i> Download</a></div>` + trailingPunctuation;
      }
      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="underline text-indigo-500 hover:text-indigo-600">${cleanUrl}</a>` + trailingPunctuation;
    }
    return match;
  });

  return buildChatMessageBlocksHtml(withBold);
}

function buildChatMessageBlocksHtml(withBold: string): string {
  type Block =
    | { kind: 'p'; lines: string[] }
    | { kind: 'ul'; items: string[] }
    | { kind: 'ol'; items: string[] };

  const rawLines = withBold.split('\n');
  const lines = rawLines.map(line => {
    // If the line starts with a bullet marker and contains the image container, strip the bullet marker
    if (/^\s*[-*•]\s+<div class="tg-chat-image-container"/.test(line)) {
      return line.replace(/^\s*[-*•]\s+/, '');
    }
    return line;
  });

  const blocks: Block[] = [];
  let para: string[] = [];
  let i = 0;

  const flushPara = (): void => {
    if (para.length === 0) {
      return;
    }
    blocks.push({ kind: 'p', lines: [...para] });
    para = [];
  };

  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      flushPara();
      i++;
      continue;
    }
    const bullet = line.match(/^\s*[-*•]\s+(.+)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.+)$/);
    if (bullet) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i]!.match(/^\s*[-*•]\s+(.+)$/);
        if (!m) {
          break;
        }
        items.push(m[1]!);
        i++;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }
    if (numbered) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i]!.match(/^\s*\d+\.\s+(.+)$/);
        if (!m) {
          break;
        }
        items.push(m[1]!);
        i++;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }
    para.push(line);
    i++;
  }
  flushPara();

  if (blocks.length === 0) {
    return '<div class="tg-chat-msg"></div>';
  }

  const parts: string[] = ['<div class="tg-chat-msg">'];
  for (const b of blocks) {
    if (b.kind === 'p') {
      const inner = b.lines.join('<br/>');
      parts.push(`<p class="tg-chat-p">${inner}</p>`);
    } else if (b.kind === 'ul') {
      const lis = b.items.map((it) => `<li class="tg-chat-li">${it}</li>`).join('');
      parts.push(`<ul class="tg-chat-ul">${lis}</ul>`);
    } else {
      const lis = b.items.map((it) => `<li class="tg-chat-li">${it}</li>`).join('');
      parts.push(`<ol class="tg-chat-ol">${lis}</ol>`);
    }
  }
  parts.push('</div>');
  return parts.join('');
}

